import React, { useEffect, useState } from 'react';
import { Redirect, useHistory, useLocation } from 'react-router-dom';
import { observer } from 'mobx-react';
import { Typography } from 'antd';
import UIStore from '../../stores/UIStore';
import { GAEvent, PageView } from '../../utils/tracking';

import '../../utils/pdfWorker';
import { QuoteOneTrueForm } from '@partsbadger/one-true-form';
import UserStore from '../../stores/UserStore';
import QuoteStore from '../../stores/QuoteStore';
import PortalLoading from 'components/PortalLoading';
import { PageHeader } from 'components/PageHeader';
import api from '../../utils/api';
import { QuoteProps } from '../../types/types';
import RedirectPreservingUTMQuery from '../../common/RedirectPreservingUTMQuery';

const { Title, Text } = Typography;

function useQuery() {
    return new URLSearchParams(useLocation().search);
}

const RapidQuoteV2 = observer(() => {
    const [loading, setLoading] = useState(true);
    const history = useHistory();
    const params = useQuery();

    const [quoteToken, setQuoteToken] = useState<any>(localStorage.getItem('quote_token'));
    const [quote, setQuote] = useState<any>(null);

    /**
     * Loads a quotes based on a signed uid
     */

    const loadQuote = () => {
        setLoading(true);

        QuoteStore.getQuoteByUid(quoteToken)
            .then(quote => {
                setQuote(quote);
            })
            .catch(() => {
                localStorage.removeItem('quote_token');
            })
            .finally(() => setLoading(false));
    };

    async function fetchQuoteUsingQuoteToken(quoteTokenUid: string) {
        return await api.requests
            .getWithoutHeader(`/customer/quote/${quoteTokenUid}/`)
            .then((data: QuoteProps) => {
                return data;
            })
            .catch(err => {
                localStorage.removeItem('quote_token');
            });
    }

    const handleQuoteToken = async (quoteToken: string) => {
        if (quoteToken && UserStore.isAuthenticated) {
            try {
                const getQuoteTokenData = await fetchQuoteUsingQuoteToken(quoteToken);
                const userData = UserStore.user;

                if (getQuoteTokenData && userData && userData.email !== getQuoteTokenData.email) {
                    await UserStore.logoutAndClearSession();
                    setQuote(getQuoteTokenData);
                    setLoading(false);
                    return true;
                }

                // Redirect users if they are logged in
                if (UserStore.isAuthenticated) {
                    window.localStorage.removeItem('quote_token');
                    return history.push('/my-account/quotes/new-quote/');
                }
            } catch (error) {
                console.error('Error fetching quote token data:', error);
            }
        }

        if (quoteToken && !UserStore.isAuthenticated) {
            loadQuote();
        } else {
            setLoading(false);
        }
    };

    /**
     * Create a new quote
     * @param user_info
     */

    useEffect(() => {
        UIStore.setSelectedKey(['Rapid Quote']);
        UIStore.setDocumentTitle('Rapid Quote');

        const utm_source = params.get('utm_source');

        if (utm_source) {
            sessionStorage.setItem('utm_source', utm_source);
        }

        handleQuoteToken(quoteToken);
    }, []);

    return (
        <>
            {loading ? (
                <PortalLoading />
            ) : (
                <>
                    {!quoteToken || !quote ? (
                        <RedirectPreservingUTMQuery to="/rapid-quote/start" />
                    ) : (
                        <>
                            <PageHeader>
                                <div className="d-flex flex-column align-items-baseline">
                                    <Title level={1} className="mb-2">
                                        {quote?.first_name ? `Welcome, ${quote?.first_name}` : `Welcome`}
                                    </Title>
                                    <Text className="font-size-md">Let's Start Your Quote</Text>
                                    {/* <Title level={1} className="mb-0">
                                            New Quote
                                        </Title> */}
                                </div>
                            </PageHeader>
                            <div className="container">
                                <QuoteOneTrueForm
                                    acquisition_source={'Portal'}
                                    quote={quote}
                                    signed_uid={quoteToken}
                                    for_staff={false}
                                    onQuoteSubmitted={(data: any) => {
                                        PageView('/rapid-quote/success/');
                                        GAEvent('Form', 'Rapid Quote Submitted');

                                        history.push('/rapid-quote/success/?next=/my-account/quotes/', {
                                            isNew: data.isNew,
                                            first_name: quote?.first_name,
                                            last_name: quote?.last_name,
                                            email: quote?.email,
                                            company: quote?.company ? quote?.company : quote?.account,
                                        });
                                        // Key that we want to keep in localStorage
                                        Object.keys(localStorage).forEach(key => {
                                            // If the key is not the one we want to keep, remove it
                                            if (key !== 'utm_params') {
                                                window.localStorage.removeItem(key);
                                            }
                                        });
                                    }}
                                />
                            </div>
                        </>
                    )}
                </>
            )}
        </>
    );
});

export default RapidQuoteV2;
